import { useLocation, useNavigate } from "react-router-dom";
import useScrollDetection from "../../../hooks/useScrollDetection";
import { IoArrowBack } from "react-icons/io5";
import { PiPencil } from "react-icons/pi";
import { IoChevronForward } from "react-icons/io5";
import { useQuery } from "@tanstack/react-query";
import { getCartForTransaction } from "../../../services/purchaseApi";
import { useEffect, useState } from "react";
import HOC from "../../../components/HOC";
import SwipeShippingOption from "../../../components/swipeComp/SwipeShippingOption";
import DesktopShippingOptionModal from "../../../components/modalComp/DesktopShippingOptionModal";

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const formatShippingEstimationDisplay = (raw) => {
  if (raw == null || String(raw).trim() === "") return "-";
  let s = String(raw).trim();

  if (/\b(hour|hours|minute|minutes|jam)\b/i.test(s)) {
    let out = s.replace(/\bhours?\b/gi, "jam");
    out = out.replace(/\bminutes?\b/gi, "menit");
    out = out.replace(/\bjam\b/gi, "jam");
    out = out.replace(/\bjam\b/g, "Jam");
    out = out.replace(/\bmenit\b/g, "Menit");
    return out;
  }

  if (/^\d+(-\d+)?$/.test(s)) {
    return `${s} Hari`;
  }

  return s;
};

const SHIPPING_GROUP_LABELS = {
  instant: "Instan",
  sameday: "Same Day",
  regular: "Reguler",
  next_day: "Next Day",
  ekonomi: "Ekonomi",
  cargo: "Kargo",
};

const buildShippingGroups = (shippingRates) => {
  if (!shippingRates) return [];
  const groups = [];
  if (shippingRates.instant?.length > 0)
    groups.push({
      key: "instant",
      label: "Instan",
      options: shippingRates.instant,
    });
  if (shippingRates.sameday?.length > 0)
    groups.push({
      key: "sameday",
      label: "Same Day",
      options: shippingRates.sameday,
    });
  if (shippingRates.regular?.length > 0)
    groups.push({
      key: "regular",
      label: "Reguler",
      options: shippingRates.regular,
    });
  if (shippingRates.next_day?.length > 0)
    groups.push({
      key: "next_day",
      label: "Next Day",
      options: shippingRates.next_day,
    });
  if (shippingRates.ekonomi?.length > 0)
    groups.push({
      key: "ekonomi",
      label: "Ekonomi",
      options: shippingRates.ekonomi,
    });
  if (shippingRates.cargo?.length > 0)
    groups.push({ key: "cargo", label: "Kargo", options: shippingRates.cargo });
  return groups;
};

const SHIPPING_RATE_BUCKET_KEYS = [
  "instant",
  "sameday",
  "regular",
  "next_day",
  "ekonomi",
  "cargo",
];

const findShippingGroupForOption = (shippingRates, option) => {
  if (!shippingRates || !option) return "regular";
  if (option.group) return option.group;

  for (const key of SHIPPING_RATE_BUCKET_KEYS) {
    const list = shippingRates[key];
    if (!Array.isArray(list) || list.length === 0) continue;
    const found = list.some(
      (o) =>
        o.code === option.code &&
        String(o.service_type ?? "") === String(option.service_type ?? ""),
    );
    if (found) return key;
  }

  const st = String(option.service_type ?? "").toLowerCase();
  if (st === "instant") return "instant";
  if (st.includes("sameday") || st === "sd") return "sameday";
  return "regular";
};

const buildShippingFieldsFromOption = (option, group) => {
  const isInstant = group === "instant";
  const rawEstimation = option.estimation ?? option.etd ?? "";
  return {
    shipping_method_id: 0,
    shipping_method_name: isInstant
      ? option.name
      : option.service_name || option.name,
    shipping_price: option.price,
    shipping_admin_fee: option.admin_fee ?? 0,
    shipment_code: option.code,
    shipment_type: isInstant ? "instant" : "express",
    shipping_provider: option.provider,
    shipping_service_type: option.service_type || "",
    shipping_service_name: isInstant
      ? option.name
      : option.service_name || option.name,
    shipping_group_code: group,
    shipping_estimation_display: formatShippingEstimationDisplay(rawEstimation),
  };
};

const DesktopSkeletonLoader = () => {
  return (
    <div className="hidden md:flex gap-6 items-start animate-pulse">
      <div className="flex-1 min-w-0 space-y-5">
        {[1, 2, 3].map((section) => (
          <div
            key={section}
            className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm"
          >
            <div className="h-6 w-44 bg-slate-200 rounded-md mb-4"></div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-3">
              <div className="h-5 w-2/3 bg-slate-200 rounded-md"></div>
              <div className="h-4 w-full bg-slate-200 rounded-md"></div>
              <div className="h-4 w-1/2 bg-slate-200 rounded-md"></div>
            </div>
          </div>
        ))}
      </div>

      <aside className="w-[380px] shrink-0 sticky top-[86px]">
        <div className="space-y-5">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <div className="h-6 w-40 bg-slate-200 rounded-md mb-4"></div>
            <div className="w-full border border-slate-200 rounded-2xl p-4 space-y-2">
              <div className="h-5 w-1/2 bg-slate-200 rounded-md"></div>
              <div className="h-4 w-1/3 bg-slate-200 rounded-md"></div>
              <div className="h-4 w-1/4 bg-slate-200 rounded-md"></div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <div className="h-6 w-36 bg-slate-200 rounded-md mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((r) => (
                <div key={r} className="flex justify-between">
                  <div className="h-4 w-24 bg-slate-200 rounded-md"></div>
                  <div className="h-4 w-28 bg-slate-200 rounded-md"></div>
                </div>
              ))}
              <hr className="my-2" />
              <div className="flex justify-between">
                <div className="h-5 w-16 bg-slate-200 rounded-md"></div>
                <div className="h-5 w-32 bg-slate-200 rounded-md"></div>
              </div>
            </div>
            <div className="mt-5 h-12 w-full rounded-xl bg-slate-200"></div>
          </div>
        </div>
      </aside>
    </div>
  );
};

const CheckoutPurchaseNew = () => {
  const machineId = localStorage.getItem("MachineId");
  const companyId = localStorage.getItem("companyId");
  const navigate = useNavigate();
  const isScrolled = useScrollDetection(5);
  const location = useLocation();
  const { type, product } = location.state ?? {};

  const [isShippingPopupOpen, setIsShippingPopupOpen] = useState(false);
  const [isDesktopShippingModalOpen, setIsDesktopShippingModalOpen] =
    useState(false);
  const [tempSelectedShipping, setTempSelectedShipping] = useState(null);

  const { data: checkoutApiData, isPending } = useQuery({
    queryKey: ["transaction-carts", companyId, type],
    queryFn: () =>
      getCartForTransaction({
        uuid: machineId,
        company_id: Number(companyId),
        product_id: type === 2 && product ? product.product_id : 0,
        quantity: type === 2 && product ? product.quantity : 0,
        action_type: Number(type) || 0,
      }),
    enabled: !!type,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  const [selectedMethod, setSelectedMethod] = useState({
    data: {
      payment_method_id: null,
      payment_method_name: "",
      shipping_method_id: null,
      shipping_method_name: "",
      shipping_price: 0,
      shipping_admin_fee: 0,
      note: "",
      fee: 0,
      ppn: 0,
      total_transaction: 0,
      shipment_code: "",
      shipment_type: "",
      shipping_provider: "",
      shipping_service_type: "",
      shipping_service_name: "",
      shipping_group_code: "",
      shipping_estimation_display: "",
    },
    cart: [],
  });

  const handleGoToPayment = () => {
    navigate("/purchase/checkout/payment-method", {
      state: {
        type: 1,
        product,
        productType: type,
        checkoutData: selectedMethod,
        cartApiData: checkoutApiData,
      },
    });
  };

  useEffect(() => {
    if (checkoutApiData) {
      const updates = {};
      if (checkoutApiData.cart) updates.cart = checkoutApiData.cart;

      if (checkoutApiData.shipping_rates?.cheapest) {
        const cheapest = checkoutApiData.shipping_rates.cheapest;
        const group = findShippingGroupForOption(
          checkoutApiData.shipping_rates,
          cheapest,
        );
        updates.data = buildShippingFieldsFromOption(cheapest, group);
      }

      setSelectedMethod((prev) => ({
        ...prev,
        ...updates,
        data: { ...prev.data, ...(updates.data || {}) },
      }));
    }
  }, [checkoutApiData]);

  const shippingGroups = buildShippingGroups(checkoutApiData?.shipping_rates);

  const selectedShippingKey =
    selectedMethod.data.shipment_code && selectedMethod.data.shipping_group_code
      ? `${selectedMethod.data.shipment_code}-${selectedMethod.data.shipping_service_type}-${selectedMethod.data.shipping_group_code}`
      : null;

  const getOptionKey = (option, group) =>
    `${option.code}-${option.service_type || ""}-${group}`;

  const handleConfirmShipping = () => {
    if (tempSelectedShipping) {
      setSelectedMethod((prev) => ({
        ...prev,
        data: { ...prev.data, ...tempSelectedShipping },
      }));
    }
    setIsShippingPopupOpen(false);
    setIsDesktopShippingModalOpen(false);
    setTempSelectedShipping(null);
  };

  const handleOpenShippingPopup = () => {
    setTempSelectedShipping(null);
    setIsShippingPopupOpen(true);
  };

  const handleOpenDesktopShippingModal = () => {
    setTempSelectedShipping(null);
    setIsDesktopShippingModalOpen(true);
  };

  const isLoading = isPending;

  return (
    <div className="w-full md:mt-4">
      <div className="hidden md:block mb-6 my-8">
        <nav className="text-base text-slate-600 mb-3 flex flex-wrap items-center gap-1 font-medium">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="hover:text-slate-900 hover:underline transition-colors"
          >
            Beranda
          </button>
          <span className="text-slate-400">/</span>
          <button
            type="button"
            onClick={() => navigate("/purchase/cart-details")}
            className="hover:text-slate-900 hover:underline transition-colors"
          >
            Keranjang
          </button>
          <span className="text-slate-400">/</span>
          <span className="text-primary">Checkout</span>
        </nav>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold text-slate-800 tracking-tight">
              Checkout
            </h1>
            <p className="text-slate-500 mt-1 text-sm lg:text-base">
              Periksa detail pesanan sebelum lanjut pembayaran.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="shrink-0 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white/80 px-4 py-2 text-md font-medium text-slate-700 shadow-sm hover:bg-secondaryActive hover:border-secondary/30 hover:text-secondary transition-colors"
          >
            <IoArrowBack className="text-slate-500" size={18} />
            Kembali
          </button>
        </div>
      </div>
      <div className="md:hidden">
        <div className="flex flex-row justify-center h-screen">
          <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
            <div className="rounded-md py-4 px-3 lg:p-8 w-full h-fit">
              <div className="w-full">
                {checkoutApiData &&
                !isLoading &&
                checkoutApiData.outlet_info ? (
                  <div className="mb-4">
                    <h1 className="font-semibold text-lg mb-1 text-slate-700">
                      Alamat Pengirim
                    </h1>
                    <div className="w-full rounded-xl border border-gray-300 bg-gray-100 flex flex-col p-3">
                      <div className="text-gray-500">
                        <h2 className="font-semibold text-base text-slate-700">
                          {checkoutApiData.outlet_info.outlet_name}
                        </h2>
                        <p className="text-md font-medium">
                          {checkoutApiData.outlet_info.address}
                        </p>
                        <p className="text-md">
                          <span className="font-semibold">
                            {checkoutApiData.outlet_info.distance}km
                          </span>{" "}
                          dari lokasi anda
                        </p>
                      </div>
                    </div>
                  </div>
                ) : isLoading ? (
                  <div className="mb-4 animate-pulse">
                    <div className="h-5 w-1/2 mb-2 text-slate-200 bg-slate-200 rounded-md"></div>
                    <div className="w-full rounded-xl border border-gray-300 bg-gray-100 flex flex-col p-3">
                      <div className="h-5 w-2/3 mb-1.5 text-slate-200 bg-slate-200 rounded-md"></div>
                      <div className="h-3 w-1/3 mb-1 text-slate-200 bg-slate-200 rounded-md"></div>
                      <div className="h-3 w-1/2 text-slate-200 bg-slate-200 rounded-md"></div>
                    </div>
                  </div>
                ) : null}

                {checkoutApiData &&
                !isLoading &&
                checkoutApiData.location_info ? (
                  <div className="mb-4">
                    <h1 className="font-semibold text-lg mb-1 text-slate-700">
                      Alamat Penerima
                    </h1>
                    <div className="w-full rounded-xl border border-gray-300 bg-gray-100 flex flex-col p-3">
                      <div className="flex items-center gap-1 mb-1">
                        <h1 className="font-semibold text-slate-700">
                          {checkoutApiData.location_info.username}
                        </h1>
                      </div>
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-md">
                            {checkoutApiData.location_info.phone_number}
                          </p>
                          <p className="text-md text-gray-500 break-words">
                            {checkoutApiData.location_info.address}
                          </p>
                        </div>
                        <div className="flex gap-1 self-end">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                "/location-address/my-address?nearby=false",
                              );
                            }}
                            className="border border-gray-300 rounded-lg p-2 cursor-pointer bg-white hover:bg-gray-50"
                          >
                            <PiPencil size={18} className="text-gray-500" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : isLoading ? (
                  <div className="mb-4 animate-pulse">
                    <div className="h-5 w-1/2 mb-2 text-slate-200 bg-slate-200 rounded-md"></div>
                    <div className="w-full rounded-xl border border-gray-300 bg-gray-100 flex flex-col p-3">
                      <div className="h-5 w-2/3 mb-1.5 text-slate-200 bg-slate-200 rounded-md"></div>
                      <div className="flex justify-between items-end gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="h-3 w-1/3 mb-1 text-slate-200 bg-slate-200 rounded-md"></div>
                          <div className="h-3 w-full mb-1 text-slate-200 bg-slate-200 rounded-md"></div>
                          <div className="h-3 w-full mb-1 text-slate-200 bg-slate-200 rounded-md"></div>
                          <div className="h-3 w-1/2 text-slate-200 bg-slate-200 rounded-md"></div>
                        </div>
                        <div className="h-8 w-8 text-slate-200 bg-slate-200 rounded-md"></div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {checkoutApiData &&
                !isLoading &&
                checkoutApiData.shipping_rates ? (
                  <div className="mb-4">
                    <h1 className="font-semibold text-lg mb-1 text-slate-700">
                      Opsi Pengiriman
                    </h1>
                    <div
                      onClick={handleOpenShippingPopup}
                      className="w-full rounded-xl border border-gray-300 bg-gray-100 flex items-center justify-between p-3 cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      {selectedMethod.data.shipment_code ? (
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-white">
                              {SHIPPING_GROUP_LABELS[
                                selectedMethod.data.shipping_group_code
                              ] || selectedMethod.data.shipping_group_code}
                            </span>
                            <p className="font-semibold text-slate-700 text-base">
                              {selectedMethod.data.shipping_service_name ||
                                selectedMethod.data.shipping_method_name}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
                            <p className="text-md font-medium text-gray-500 mt-0.5 tabular-nums">
                              Rp
                              {formatRupiah(selectedMethod.data.shipping_price)}
                            </p>
                            {selectedMethod.data.shipping_estimation_display ? (
                              <p className="text-md text-gray-500 mt-0.5">
                                <span className="text-gray-400">·</span>{" "}
                                {
                                  selectedMethod.data
                                    .shipping_estimation_display
                                }
                              </p>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 text-md">
                          Pilih metode pengiriman
                        </p>
                      )}
                      <IoChevronForward
                        size={18}
                        className="text-gray-400 flex-shrink-0"
                      />
                    </div>
                  </div>
                ) : isLoading ? (
                  <div className="mb-4 animate-pulse">
                    <div className="h-5 w-1/2 mb-2 bg-slate-200 rounded-md"></div>
                    <div className="w-full rounded-xl border border-gray-300 bg-gray-100 flex items-center justify-between p-3">
                      <div className="flex flex-col gap-1 flex-1">
                        <div className="h-4 w-1/3 bg-slate-200 rounded-md"></div>
                        <div className="h-3 w-1/4 bg-slate-200 rounded-md"></div>
                      </div>
                      <div className="h-5 w-5 bg-slate-200 rounded-full"></div>
                    </div>
                  </div>
                ) : null}

                {checkoutApiData &&
                !isLoading &&
                checkoutApiData.cart &&
                checkoutApiData.result ? (
                  <div className="mb-4">
                    <h1 className="font-semibold text-lg mb-1 text-slate-700">
                      Ringkasan Pesanan
                    </h1>
                    <div className="w-full rounded-xl border border-gray-300 bg-white flex flex-col p-3">
                      <h1 className="font-semibold text-slate-700 mb-2">
                        Daftar Obat
                      </h1>
                      <div className="flex flex-col gap-1">
                        {checkoutApiData.cart.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-end justify-between"
                          >
                            <div className="flex flex-col">
                              <p className="font-medium text-slate-700 line-clamp-1 leading-tight text-md">
                                {item.product_name}
                              </p>
                              <p className="text-sm text-gray-500 tabular-nums">
                                {item.quantity}x Rp
                                {formatRupiah(item.price)}
                              </p>
                            </div>
                            <p className="font-semibold text-slate-700 text-md tabular-nums">
                              Rp{formatRupiah(item.quantity * item.price)}
                            </p>
                          </div>
                        ))}
                      </div>
                      <hr className="my-2" />
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-700 text-md">
                            Subtotal Produk
                          </p>
                          <p className="font-semibold text-slate-700 text-md tabular-nums">
                            Rp
                            {formatRupiah(
                              checkoutApiData.cart.reduce(
                                (total, item) =>
                                  total + item.quantity * item.price,
                                0,
                              ),
                            )}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-700 text-md">
                            Total Diskon
                          </p>
                          <p className="font-semibold text-slate-700 text-md tabular-nums">
                            Rp
                            {formatRupiah(checkoutApiData.result.discount)}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-700 text-md">
                            Biaya Pengiriman
                          </p>
                          <p className="font-semibold text-slate-700 text-md tabular-nums">
                            Rp
                            {formatRupiah(
                              selectedMethod.data.shipping_price ||
                                checkoutApiData.result.shipping,
                            )}
                          </p>
                        </div>
                      </div>
                      <hr className="my-2" />
                      <div className="flex items-center justify-between">
                        <h1 className="font-semibold text-slate-700">Total</h1>
                        <p className="font-semibold text-error tabular-nums">
                          Rp
                          {formatRupiah(
                            checkoutApiData.cart.reduce(
                              (total, item) =>
                                total + item.quantity * item.price,
                              0,
                            ) -
                              checkoutApiData.result.discount +
                              (selectedMethod.data.shipping_price ||
                                checkoutApiData.result.shipping),
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : isLoading ? (
                  <div className="mb-4 animate-pulse">
                    <div className="h-5 w-1/2 mb-2 bg-slate-200 rounded-md"></div>
                    <div className="w-full rounded-xl border border-gray-300 bg-white flex flex-col p-3">
                      <div className="h-5 w-2/3 mb-1.5 text-slate-200 bg-slate-200 rounded-md"></div>
                      <div className="flex flex-col gap-1">
                        {[1, 2, 3].map((item) => (
                          <div
                            key={item}
                            className="w-full flex items-end justify-between"
                          >
                            <div className="flex flex-col w-full">
                              <div className="h-4 w-1/3 mb-1.5 bg-slate-200 rounded-md"></div>
                              <div className="h-3 w-1/2 bg-slate-200 rounded-md"></div>
                            </div>
                            <div className="h-5 w-20 bg-slate-200 rounded-md"></div>
                          </div>
                        ))}
                      </div>
                      <hr className="my-2" />
                      <div className="flex flex-col gap-1">
                        {[1, 2, 3].map((item) => (
                          <div
                            key={item}
                            className="flex items-center justify-between"
                          >
                            <div className="h-5 w-1/2 bg-slate-200 rounded-md"></div>
                            <div className="h-5 w-1/3 bg-slate-200 rounded-md"></div>
                          </div>
                        ))}
                      </div>
                      <hr className="my-2" />
                      <div className="flex items-center justify-between">
                        <div className="h-5 w-1/3 bg-slate-200 rounded-md"></div>
                        <div className="h-5 w-1/3 bg-slate-200 rounded-md"></div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <DesktopSkeletonLoader />
      ) : (
        <div className="hidden md:flex gap-4 items-start text-base">
          <div className="flex-1 min-w-0 space-y-5">
            {checkoutApiData?.outlet_info && (
              <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                  Alamat Pengirim
                </h2>

                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                  <p className="font-semibold text-slate-700">
                    {checkoutApiData.outlet_info.outlet_name}
                  </p>

                  <p className="text-slate-500 mt-1">
                    {checkoutApiData.outlet_info.address}
                  </p>

                  <p className="text-md text-slate-500 mt-2">
                    {checkoutApiData.outlet_info.distance} km dari lokasi anda
                  </p>
                </div>
              </div>
            )}

            {checkoutApiData?.location_info && (
              <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-slate-800">
                    Alamat Penerima
                  </h2>

                  <button
                    onClick={() =>
                      navigate("/location-address/my-address?nearby=false")
                    }
                    className="text-secondary text-base font-medium hover:underline"
                  >
                    Ubah
                  </button>
                </div>

                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                  <p className="font-semibold text-slate-700">
                    {checkoutApiData.location_info.username}
                  </p>

                  <p className="text-primary font-medium mt-1">
                    {checkoutApiData.location_info.phone_number}
                  </p>

                  <p className="text-slate-500 mt-1 text-md break-words">
                    {checkoutApiData.location_info.address}
                  </p>
                </div>
              </div>
            )}

            {checkoutApiData?.cart && (
              <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                  Ringkasan Pesanan
                </h2>

                <div className="space-y-4">
                  {checkoutApiData.cart.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-end border-b border-slate-100 pb-4"
                    >
                      <div>
                        <p className="font-medium text-slate-700">
                          {item.product_name}
                        </p>

                        <p className="text-base text-slate-500 mt-1">
                          {item.quantity} x Rp{formatRupiah(item.price)}
                        </p>
                      </div>

                      <p className="font-semibold text-lg text-slate-700">
                        Rp{formatRupiah(item.quantity * item.price)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="w-[380px] shrink-0 sticky top-[82px]">
            <div className="space-y-5">
              <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                  Opsi Pengiriman
                </h2>

                <button
                  onClick={handleOpenDesktopShippingModal}
                  className="w-full border border-slate-200 rounded-2xl p-4 text-left hover:bg-slate-50 transition text-lg"
                >
                  {selectedMethod.data.shipment_code ? (
                    <>
                      <p className="font-semibold text-slate-700">
                        {selectedMethod.data.shipping_service_name}
                      </p>

                      <p className="text-lg font-medium text-slate-500 my-1">
                        Rp
                        {formatRupiah(selectedMethod.data.shipping_price)}
                      </p>

                      <p className="text-md text-slate-500">
                        {selectedMethod.data.shipping_estimation_display}
                      </p>
                    </>
                  ) : (
                    <p className="text-slate-400">Pilih metode pengiriman</p>
                  )}
                </button>
              </div>

              <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                  Detail Harga
                </h2>

                <div className="space-y-2 text-base">
                  <div className="flex justify-between">
                    <p className="text-slate-500">Subtotal</p>
                    <p className="font-medium text-slate-700 tabular-nums">
                      Rp
                      {formatRupiah(
                        checkoutApiData?.cart?.reduce(
                          (acc, item) => acc + item.quantity * item.price,
                          0,
                        ),
                      )}
                    </p>
                  </div>

                  <div className="flex justify-between">
                    <p className="text-slate-500">Diskon</p>
                    <p className="font-medium text-slate-700 tabular-nums">
                      Rp
                      {formatRupiah(checkoutApiData?.result?.discount)}
                    </p>
                  </div>

                  <div className="flex justify-between">
                    <p className="text-slate-500">Pengiriman</p>
                    <p className="font-medium text-slate-700 tabular-nums">
                      Rp
                      {formatRupiah(selectedMethod.data.shipping_price)}
                    </p>
                  </div>

                  <hr className="my-2" />

                  <div className="flex justify-between text-base">
                    <p className="font-semibold text-slate-800">Total</p>

                    <p className="font-semibold text-error tabular-nums">
                      Rp
                      {formatRupiah(
                        checkoutApiData?.cart?.reduce(
                          (acc, item) => acc + item.quantity * item.price,
                          0,
                        ) -
                          checkoutApiData?.result?.discount +
                          selectedMethod.data.shipping_price,
                      )}
                    </p>
                  </div>
                </div>

                <button
                  disabled={!selectedMethod.data.shipment_code}
                  onClick={handleGoToPayment}
                  className="mt-5 h-12 text-lg w-full rounded-xl bg-secondary text-white font-medium hover:bg-secondaryHover disabled:bg-slate-300"
                >
                  Pilih Pembayaran
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      <div className="text-base fixed md:hidden bottom-0 left-0 right-0 bg-white rounded-t-3xl py-4 px-3 z-[99] shadow-2xl">
        <button
          disabled={!selectedMethod.data.shipment_code}
          onClick={handleGoToPayment}
          className="h-[40px] border border-secondary flex items-center justify-center bg-secondary text-white w-full rounded-lg disabled:bg-slate-300"
        >
          Pilih Pembayaran
        </button>
      </div>

      <SwipeShippingOption
        open={isShippingPopupOpen}
        onClose={() => setIsShippingPopupOpen(false)}
        formatShippingEstimationDisplay={formatShippingEstimationDisplay}
        tempSelectedShipping={tempSelectedShipping}
        setTempSelectedShipping={setTempSelectedShipping}
        buildShippingFieldsFromOption={buildShippingFieldsFromOption}
        shippingGroups={shippingGroups}
        selectedShippingKey={selectedShippingKey}
        getOptionKey={getOptionKey}
        handleConfirm={handleConfirmShipping}
        handleConfirmShipping={handleConfirmShipping}
      />

      <DesktopShippingOptionModal
        open={isDesktopShippingModalOpen}
        onClose={() => setIsDesktopShippingModalOpen(false)}
        formatShippingEstimationDisplay={formatShippingEstimationDisplay}
        tempSelectedShipping={tempSelectedShipping}
        setTempSelectedShipping={setTempSelectedShipping}
        buildShippingFieldsFromOption={buildShippingFieldsFromOption}
        shippingGroups={shippingGroups}
        selectedShippingKey={selectedShippingKey}
        getOptionKey={getOptionKey}
        handleConfirmShipping={handleConfirmShipping}
      />
    </div>
  );
};

export default HOC(CheckoutPurchaseNew, "required-auth");
